/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as React from 'react';

import { plural } from '@cian/utils';
import { IGetVillageListRequestSchema } from '../../../../node/repositories/countryside-search/entities/schemas/GetVillageListRequestSchema';

import { COMMUTE_TYPES_MAP, OBJECT_VARIANTS } from '../../../interfaces/villages';
import { IVillageCounterSetSchema } from '../../../repositories/countryside-search/entities/counter/VillageCounterSetSchema';
import { IRoadInfoSchema } from '../../../repositories/countryside-search/entities/road/RoadInfoSchema';
import { IStationInfoSchema } from '../../../repositories/countryside-search/entities/station_info/StationInfoSchema';
import { IVillageForListPresenterSchema } from '../../../repositories/countryside-search/entities/village/VillageForListPresenterSchema';
import { INFO_DELIMITER, isArrayWithItems, NON_BREAKING_SPACE } from '../../../utils';
import { getGeoRegionId } from '../../../utils/geo';
import { getVillageOffersType } from '../../../utils/getVillageOffersType';
import { CommuteSet, ECommuteSetType } from '../commut_set';
import { trackHighwayClicked, trackRailwayClicked } from '../tracking';

export const getYearRange = (yearMin: number, yearMax: number) => {
  return yearMin === yearMax ? yearMin.toString() : `${yearMin}—${yearMax}`;
};

export const getCounterSetOnSale = (counterSet: IVillageCounterSetSchema | null | undefined) => {
  if (!counterSet) {
    return 0;
  }

  const { onSaleTownhouse, onSaleLowrisehouse, onSaleLandplot, onSaleCottage, onSaleHouseShare } = counterSet;

  return onSaleTownhouse! + onSaleLowrisehouse! + onSaleLandplot! + onSaleCottage! + onSaleHouseShare!;
};

export const getTotalObjects = (total: number) => `${total} ${plural(total, OBJECT_VARIANTS)}`;

export const getVillageInfo = (village: IVillageForListPresenterSchema) => {
  const { counterSet, finishYearMax, finishYearMin } = village;
  if (!counterSet) {
    return [];
  }

  const offersType = getVillageOffersType(village);

  const villageInfo = [
    `Сдача${NON_BREAKING_SPACE}в${NON_BREAKING_SPACE}${getYearRange(finishYearMin as number, finishYearMax as number)}`,
  ];
  const onSale = getCounterSetOnSale(counterSet!);

  if (counterSet.total && !!offersType) {
    if (offersType !== 'resale') {
      villageInfo.push(getTotalObjects(counterSet.total));
    }

    if (onSale) {
      villageInfo.push(`${onSale}${NON_BREAKING_SPACE}в${NON_BREAKING_SPACE}продаже`);
    }
  }

  return villageInfo.join(INFO_DELIMITER);
};

export const getDistanceHighway = (highway: IRoadInfoSchema, regionId?: number) => {
  if (!highway.distanceCity) {
    return '';
  }

  return `${highway.distanceCity} км до ${(regionId && COMMUTE_TYPES_MAP[regionId]) || 'центра'}`;
};

export const getNewDistanceRailway = (station: IStationInfoSchema) => {
  const { distanceCity, terminalName, genitiveTerminalName } = station;

  if (!distanceCity || (!genitiveTerminalName && !terminalName)) {
    return '';
  }

  return `${distanceCity} км до ${genitiveTerminalName || terminalName}`;
};

export function getNewStation(station: IStationInfoSchema) {
  const { stationName, directionName, seo } = station;

  if (!stationName || !directionName) {
    return null;
  }

  const name = `${stationName} (напр. ${directionName})`;

  return (
    <CommuteSet
      title={name}
      distanceText={getNewDistanceRailway(station)}
      link={seo ? (seo.url as string) : ''}
      type={ECommuteSetType.Railway}
      trackEvent={trackRailwayClicked}
    />
  );
}

export const getStation = (
  regionHasStations: boolean,
  filters: IGetVillageListRequestSchema,
  stationList?: IStationInfoSchema[] | null,
) => {
  const station = isArrayWithItems(stationList) ? (stationList as IStationInfoSchema[])[0] : undefined;

  if (!regionHasStations || !station) {
    return null;
  }

  return getNewStation(station);
};

export const getHighway = (
  regionHasRoads: boolean,
  filters: IGetVillageListRequestSchema,
  village: IVillageForListPresenterSchema,
  isMobile = false,
) => {
  const { geo, roadInfoList } = village;

  const road = isArrayWithItems(roadInfoList) && (roadInfoList as IRoadInfoSchema[])[0];
  const regionId = getGeoRegionId(geo);

  if (!regionHasRoads || !road) {
    return null;
  }

  const title = `${road.name} шоссе`;

  return (
    <CommuteSet
      title={title}
      distanceText={getDistanceHighway(road, regionId)}
      link={isMobile ? undefined : ((road.seo && road.seo.url) as string)}
      type={isMobile ? ECommuteSetType.HighwayMobile : ECommuteSetType.Highway}
      trackEvent={isMobile ? undefined : trackHighwayClicked}
    />
  );
};
